import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deriveKeyMaterial, splitKeyMaterial, encryptPayload, messageAad } from '@aions/crypto';
import { estadoLabelDenunciante } from '@aions/core';
import { bytesToBase64 } from '../utils/bytes';
import {
  categorizeMessage,
  decryptStatusResponse,
  type DecryptedStatus,
  type DecodedMessage,
  type StatusLookupResponse,
} from '../lib/status-decrypt';
import { NotificacaoBadge } from '../components/NotificacaoBadge';
import { apiUrl } from '../lib/api-base';
import { intervaloDaSemana } from '../lib/semana';
import { uploadAttachment } from '../lib/attachments-upload';
import { Alert, dataHora } from '@aions/ui-primitives';

type ComposerState = 'idle' | 'sending' | 'sent' | 'error';

interface GrupoDeSemana {
  readonly semana: string;
  readonly itens: DecodedMessage[];
}

/**
 * Agrupa a trilha por semana para o marcador aparecer UMA vez por grupo, em vez
 * de repetir o rótulo em cada cartão.
 *
 * 🔴 Agrupa RUNS CONSECUTIVOS, de propósito — nunca reordena e nunca junta
 * ocorrências separadas da mesma semana. Num registro do rito, a ordem é o dado;
 * se a mesma semana reaparecer depois (anomalia), ela ganha um segundo marcador,
 * o que é visível e investigável. Consolidar por chave esconderia a anomalia
 * embaralhando a sequência — e a ordem do dashboard já foi efeito colateral de um
 * vazamento uma vez (LT-01 peça 2).
 */
export function agruparPorSemana(msgs: ReadonlyArray<DecodedMessage>): GrupoDeSemana[] {
  const grupos: GrupoDeSemana[] = [];
  for (const m of msgs) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo !== undefined && ultimo.semana === m.createdWeek) ultimo.itens.push(m);
    else grupos.push({ semana: m.createdWeek, itens: [m] });
  }
  return grupos;
}

/** Limite por arquivo, ESPELHO de `MAX_FILE_BYTES` do servidor (r2-presign.ts).
 *
 *  ⚠️ Duplicado de propósito, e a duplicação é declarada: o cliente precisa do número
 *  ANTES de subir, para não fazer o denunciante esperar um upload de 40 MB que termina
 *  em `SIZE_REJECTED`. Quem AUTORIZA continua sendo o servidor — esta constante nunca
 *  é a única barreira. Se ela divergir, o pior caso é a tela oferecer algo que o
 *  servidor recusa (visível), nunca o contrário. */
const MAX_ANEXO_BYTES = 25 * 1024 * 1024;

/** Tamanho legível. Sem `Intl.NumberFormat` porque o valor é aproximado por desenho —
 *  precisão de byte aqui só ocuparia espaço. */
function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Decisão e decisão de recurso são ATOS do processo, não turnos de conversa. */
function ehAtoDoProcesso(accent: string): boolean {
  return accent === 'decisao' || accent === 'recurso';
}

/**
 * O selo de cifra da caixa de mensagens.
 *
 * A afirmação é literal, não marketing: `handleResponder` e
 * `handleInfoComplementar` chamam `encryptPayload` no browser e o POST leva só
 * `{ciphertext, nonce}` — o texto claro não sai daqui. Ficava embutida no rótulo
 * do botão ("Cifrar e enviar"), onde só existia no instante do clique; como selo
 * fica visível enquanto a pessoa escreve, que é quando a dúvida pesa.
 *
 * Um componente e não JSX repetido: são duas caixas, e o SVG duplicado divergiria
 * na primeira vez que alguém mexesse em uma delas.
 */
function SeloDeCifra({ texto }: { readonly texto: string }): JSX.Element {
  return (
    <p className="composer__selo">
      {/* Cadeado decorativo — o texto ao lado já diz tudo, então o ícone fica
          fora da árvore de acessibilidade em vez de virar ruído no leitor. */}
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
      {texto}
    </p>
  );
}

export function StatusPage(): JSX.Element {
  const { t } = useTranslation();
  const [protocolo, setProtocolo] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [decoded, setDecoded] = useState<DecryptedStatus | null>(null);
  // STA-04 / REC-04: precisamos do authHash em memória para que o
  // NotificacaoBadge.onAcknowledge possa chamar POST /api/status/messages
  // (`markPhaseChangeSeen`). Não persistimos em localStorage — vive só no
  // ciclo de vida deste componente; é descartado no logout / refresh.
  const [authHashB64, setAuthHashB64] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Divergência 3 Fatia C — composers do denunciante (resposta + info-complementar).
  const [resposta, setResposta] = useState('');
  /** Fila de anexos do envio atual (dentro do limite). */
  const [arquivos, setArquivos] = useState<File[]>([]);
  /** Arquivo recusado por tamanho — dispara o bloco de instrucoes de nuvem. */
  const [arquivoGrande, setArquivoGrande] = useState<File | null>(null);
  const [respostaState, setRespostaState] = useState<ComposerState>('idle');

  const handleLookup = async () => {
    setLoading(true);
    setError('');
    setDecoded(null);
    setAuthHashB64(null);
    let kekRaw: Uint8Array | null = null;
    try {
      // Argon2id derivation client-side (ANON-03, STA-01).
      const material = await deriveKeyMaterial(protocolo, passphrase);
      const split = splitKeyMaterial(material);
      kekRaw = split.kekRaw;
      const authHash = bytesToBase64(split.authHash);

      const resp = await fetch(apiUrl('/api/status/lookup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authHash }),
      });
      if (!resp.ok) {
        // 429 (rate-limit, 🐞-1) ganha mensagem própria. D-31 anti-enumeração
        // segue intacto: o 429 é pré-lookup, não revela nada sobre o caso; só o
        // 404/timeout/decrypt (abaixo) colapsam no not_found uniforme.
        setError(resp.status === 429 ? t('status.rate_limited') : t('status.not_found'));
        return;
      }
      const data = (await resp.json()) as StatusLookupResponse;
      const out = await decryptStatusResponse(data, kekRaw);
      setDecoded(out);
      setAuthHashB64(authHash);
    } catch {
      // D-31: same not-found message regardless of failure mode.
      setError(t('status.not_found'));
    } finally {
      // Best-effort zero of KEK in JS heap (advisory — V8 may keep copies).
      if (kekRaw) kekRaw.fill(0);
      setLoading(false);
    }
  };

  /**
   * REC-04 / STA-04 — ack the badge by calling the existing POST
   * /api/status/messages endpoint (handler `markPhaseChangeSeen`). On 200,
   * we flip the local `unseenPhaseChange` flag to false so the badge
   * disappears without needing a re-lookup. Failures are silent (best-effort);
   * the user can simply re-consult to retry.
   */
  const handleAcknowledge = async () => {
    if (!authHashB64 || !decoded) return;
    try {
      const resp = await fetch(apiUrl('/api/status/messages'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authHash: authHashB64 }),
      });
      if (resp.ok) {
        setDecoded({ ...decoded, unseenPhaseChange: false });
      }
    } catch {
      // Silent — the badge stays visible; user can re-try on next consulta.
    }
  };

  /**
   * Divergência 3 Fatia C — resposta do denunciante ao comitê. Cifra sob a DEK que
   * ele JÁ tem (decoded.dekRaw, derivada do protocolo — NÃO depende de COMITE_READ),
   * AAD=messageAad(caseId, ulid) com ulid opaco (crypto.randomUUID). Texto claro
   * nunca sai do browser; o POST leva só {authHash, ulid, ciphertext, nonce}.
   * Append otimista no thread (sem re-derivar Argon2id).
   */
  /**
   * Adiciona arquivos à fila, separando os que passam do limite.
   *
   * O arquivo grande NÃO entra na fila: ele vira `arquivoGrande`, que faz aparecer o
   * bloco com as instruções de nuvem. Deixá-lo na fila e falhar no envio gastaria o
   * tempo de upload do denunciante para terminar em erro.
   */
  const adicionarArquivos = (lista: FileList | null): void => {
    if (lista === null) return;
    const novos = Array.from(lista);
    const grande = novos.find((f) => f.size > MAX_ANEXO_BYTES);
    setArquivoGrande(grande ?? null);
    const cabem = novos.filter((f) => f.size <= MAX_ANEXO_BYTES);
    if (cabem.length > 0) setArquivos((atual) => [...atual, ...cabem]);
    if (respostaState !== 'idle') setRespostaState('idle');
  };

  /**
   * UM envio: sobe os anexos e manda a mensagem.
   *
   * ── Ordem: anexos ANTES da mensagem
   *
   * Se um upload falhar, nada é enviado e o denunciante vê erro com os arquivos ainda
   * na fila — pode tentar de novo. A ordem inversa deixaria a mensagem no thread
   * dizendo "segue documento" com o documento ausente, que é pior que não enviar:
   * o comitê agiria sobre uma promessa não cumprida.
   *
   * ── Por que a lista de nomes vai DENTRO da mensagem
   *
   * O anexo é registrado em `attachmentRefs`, e a tela do denunciante não renderiza
   * anexos (over-fetch conhecido: o campo atravessa a decifra e nenhuma tela o usa).
   * Sem o nome no texto, o envio de "só arquivo" não deixaria rastro NENHUM visível a
   * quem enviou — ele não teria como saber se funcionou. O nome vai cifrado como
   * qualquer mensagem, sob a mesma DEK.
   */
  const handleEnviar = async (): Promise<void> => {
    if (!authHashB64 || !decoded) return;
    const texto = resposta.trim();
    if (texto === '' && arquivos.length === 0) return;
    setRespostaState('sending');
    try {
      for (const f of arquivos) {
        await uploadAttachment(f, decoded.dekRaw, decoded.caseId);
      }

      const nomes = arquivos.map((f) => f.name);
      const corpo =
        nomes.length === 0
          ? texto
          : texto === ''
            ? t('status.canal_corpo_so_anexos', { lista: nomes.join(', ') })
            : `${texto}\n\n${t('status.canal_corpo_com_anexos', { lista: nomes.join(', ') })}`;

      const ulid = crypto.randomUUID();
      const enc = await encryptPayload(corpo, decoded.dekRaw, messageAad(decoded.caseId, ulid));
      const resp = await fetch(apiUrl('/api/status/mensagem'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authHash: authHashB64,
          ulid,
          ciphertext: bytesToBase64(enc.ciphertext),
          nonce: bytesToBase64(enc.nonce),
        }),
      });
      if (!resp.ok) {
        setRespostaState('error');
        return;
      }
      const { createdWeek } = (await resp.json()) as { ulid: string; createdWeek: string };
      const novaMsg: DecodedMessage = {
        ulid,
        plaintext: corpo,
        createdWeek,
        origin: 'denunciante_message',
      };
      setDecoded({ ...decoded, mockMessages: [...decoded.mockMessages, novaMsg] });
      setResposta('');
      setArquivos([]);
      setArquivoGrande(null);
      setRespostaState('sent');
    } catch {
      setRespostaState('error');
    }
  };

  // D1 — o caso encerrado não aceita mais escrita de nenhum dos dois lados; o
  // servidor devolve 422 `caso_encerrado` nas duas rotas (`/status/mensagem` e
  // `/status/info-complementar`), e aqui os composers dão lugar ao painel que
  // explica e oferece a saída que o rito permite.
  //
  // Comparação com o LITERAL, e não com uma cópia de `FASE_TERMINAL`: o servidor é
  // a fonte de verdade da regra, e duplicar a lista no cliente criaria uma segunda
  // lista para divergir — exatamente o defeito que o D1 corrigiu (o comitê fechava
  // em `Encerrada` e o denunciante não, no mesmo thread). Se um estado terminal novo
  // entrar na máquina, o servidor recusa e a tela mostra o erro do envio: falha
  // visível, não silenciosa.
  const casoEncerrado = decoded?.state.kind === 'Encerrada';

  return (
    <div className="status-page fade-in">
      <header className="status-hero">
        <span className="status-hero-icon" aria-hidden="true">
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <h1 className="status-hero-title">{t('status.title')}</h1>
      </header>

      <div className="card status-card">
        <div className="form-group">
          <label className="form-label" htmlFor="status-protocolo">
            {t('status.protocolo_label')}
          </label>
          <input
            id="status-protocolo"
            className="form-input"
            value={protocolo}
            onChange={(e) => setProtocolo(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="status-passphrase">
            {t('status.passphrase_label')}
          </label>
          <input
            id="status-passphrase"
            className="form-input"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            autoComplete="off"
          />
        </div>
        <button
          className="btn btn-primary status-submit"
          onClick={handleLookup}
          disabled={!protocolo || !passphrase || loading}
        >
          {loading ? <span className="spinner" /> : null}
          {t('status.consultar')}
        </button>
        {error && (
          <div
            className="alert alert-warning"
            role="alert"
            aria-live="polite"
            style={{ marginTop: 16 }}
          >
            {error}
          </div>
        )}
      </div>

      {decoded && (
        <div className="card status-result-card fade-in">
          {/* REC-04 / STA-04 — badge genérico no topo (anonimato preservado) */}
          <NotificacaoBadge unseen={decoded.unseenPhaseChange} onAcknowledge={handleAcknowledge} />
          <div className="status-result-head">
            <span className="review-label">{t('status.estado_label')}</span>
            {/* Nunca renderizar `state.kind` cru: é o identificador da união de
                estados ("AguardandoRetornoComite") e esta é a tela para onde o
                denunciante volta. Fallback neutro para estado desconhecido. */}
            <span className="status-state">{estadoLabelDenunciante(decoded.state.kind)}</span>
          </div>
          <div className="review-section">
            <div className="review-label">{t('status.descricao_label')}</div>
            <div className="review-value" style={{ whiteSpace: 'pre-wrap' }}>
              {decoded.payload.descricao}
            </div>
          </div>
          {/* D4 — o filtro era `.filter(m => m.plaintext)`: item que não decifrava
              sumia da tela sem deixar rastro (medido: servidor entregou 9, a tela
              mostrou 6). Agora entra quem TEM texto **ou** quem está marcado como
              ilegível — some só o placeholder legado (ciphertext vazio), que a lib
              já descarta na camada de dados. */}
          {decoded.mockMessages.filter((m) => m.plaintext || m.falhaDecifra).length > 0 && (
            <div className="review-section">
              <div className="review-label">{t('status.mensagens_label')}</div>
              {/* <ol> porque a ordem É o dado num registro de rito; a semana é o
                  agrupamento, e cada semana é uma graduação na trilha. */}
              <ol className="trilha">
                {agruparPorSemana(
                  decoded.mockMessages.filter((m) => m.plaintext || m.falhaDecifra),
                ).map((grupo, iGrupo) => {
                  const intervalo = intervaloDaSemana(grupo.semana);
                  return (
                    <li className="trilha__grupo" key={`${grupo.semana}-${iGrupo}`}>
                      {/* A semana por extenso. `intervaloDaSemana` converte NOTAÇÃO
                          ("2026-W33"), não granularidade — a semana continua sendo
                          a unidade, e isso é deliberado (LT-01/LT-26). */}
                      <h3 className="trilha__semana">
                        {t('status.semana_rotulo', {
                          intervalo,
                          defaultValue: `Semana de ${intervalo}`,
                        })}
                      </h3>
                      <ul className="trilha__lista">
                        {grupo.itens.map((m) => {
                          const cat = categorizeMessage(m);
                          /* D4 — o item existe e não abriu. Dizer isso é o ponto: o
                             denunciante estava decidindo sobre uma conversa incompleta
                             sem saber que estava incompleta. A copy não manda ele fazer
                             nada (não há o que fazer) e fecha a única dúvida que
                             importa: o resto vale. Agora com peso visual — a classe
                             existia sem uma linha de CSS. */
                          const corpo = m.falhaDecifra ? (
                            <p data-testid="mensagem-falha-decifra" className="message-falha">
                              {t('status.mensagem_falha_decifra', {
                                defaultValue:
                                  'Esta mensagem não pôde ser aberta. O restante da sua denúncia não é afetado.',
                              })}
                            </p>
                          ) : null;

                          // ---- Ato do processo: documento, não turno de conversa.
                          if (ehAtoDoProcesso(cat.accent)) {
                            return (
                              <li key={m.ulid}>
                                <article
                                  className={`ato ato--${cat.accent}`}
                                  data-origin={m.origin ?? 'unknown'}
                                >
                                  <h4 className="ato__faixa">
                                    {t(cat.label)}
                                    {/* Data e hora do ato. Existe porque a mensagem
                                        é do COMITÊ — ato de pessoa nomeada em ata,
                                        cuja hora o LT-01 peça 3 manteve de propósito.
                                        Ausente → nada é renderizado; a semana do
                                        cabeçalho do grupo segue sendo a referência.
                                        Nunca fabricar 00:00 a partir de ausência. */}
                                    {m.createdAtEpochMs !== undefined && (
                                      <span className="ato__quando">
                                        {dataHora(m.createdAtEpochMs)}
                                      </span>
                                    )}
                                  </h4>
                                  <div className="ato__corpo">
                                    {/* REC-04 — contexto antes da carta do recurso. */}
                                    {cat.accent === 'recurso' && (
                                      <p className="ato__nota">
                                        {t('status.decisao_recurso_descricao')}
                                      </p>
                                    )}
                                    {corpo ?? <p className="ato__texto">{m.plaintext}</p>}
                                  </div>
                                </article>
                              </li>
                            );
                          }

                          // ---- Turno de conversa. A VOZ vem de `origin`, não do
                          // accent: `categorizeMessage` devolve `info` para o comitê
                          // E para o denunciante (há teste fixando isso), então o
                          // accent não distingue quem falou — o origin distingue.
                          const ehVoce = m.origin === 'denunciante_message';
                          return (
                            <li key={m.ulid}>
                              <div
                                className={`turno turno--${ehVoce ? 'voce' : 'comite'}`}
                                data-origin={m.origin ?? 'unknown'}
                              >
                                <h4 className="turno__quem">
                                  {ehVoce
                                    ? t('status.voz_voce', { defaultValue: 'Você' })
                                    : t('status.voz_comite', { defaultValue: 'Comitê' })}
                                  {/* Só os turnos do comitê carregam hora. Nos SEUS,
                                      o campo nem existe no registro: hora fina do lado
                                      do denunciante é o vetor de correlação que o LT-01
                                      fechou, então não há o que exibir — e a assimetria
                                      na tela é a defesa, não um bug. */}
                                  {m.createdAtEpochMs !== undefined && (
                                    <span className="turno__quando">
                                      {dataHora(m.createdAtEpochMs)}
                                    </span>
                                  )}
                                </h4>
                                {corpo ?? <p className="turno__texto">{m.plaintext}</p>}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
          {decoded.infoComplementar.length > 0 && (
            <div className="review-section">
              <div className="review-label">{t('status.info_complementar_label')}</div>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {decoded.infoComplementar.map((m) =>
                  m.falhaDecifra ? (
                    /* D4 — paridade com as mensagens. Aqui o defeito era pior: antes
                       do conserto na lib, UM item ilegível rejeitava o Promise.all e
                       a consulta inteira virava "nenhuma denúncia encontrada". */
                    <li key={m.ulid} data-testid="info-falha-decifra" className="message-falha">
                      {t('status.info_falha_decifra', {
                        defaultValue: 'Esta informação complementar não pôde ser aberta.',
                      })}
                    </li>
                  ) : (
                    <li key={m.ulid}>{m.plaintext}</li>
                  ),
                )}
              </ul>
            </div>
          )}

          {/* D1 — caso ENCERRADO: painel explicativo NO LUGAR dos composers.
              O `state` sempre veio do /lookup e a tela já imprimia "Encerrada" no
              rótulo — só não usava o dado aqui. O resultado era uma tela que se
              contradizia: dizia "Encerrada" e logo abaixo convidava a responder.
              O servidor recusa (422 caso_encerrado), mas erro depois do envio é
              quase tão ruim quanto aceitar em silêncio.
              A copy é v0.1 pendente de revisão editorial — ver
              `status._comment_encerrado` no i18n para as verificações feitas. */}
          {casoEncerrado && (
            <div className="review-section" data-testid="painel-caso-encerrado">
              <h3 className="review-label">{t('status.encerrado_titulo')}</h3>
              <p>{t('status.encerrado_corpo')}</p>
              <h4 className="review-label" style={{ marginTop: 16 }}>
                {t('status.encerrado_novo_titulo')}
              </h4>
              <p>{t('status.encerrado_novo_corpo')}</p>
              <a
                className="btn btn-primary"
                data-testid="acao-nova-denuncia"
                href="/"
                style={{ marginTop: 8, display: 'inline-block' }}
              >
                {t('status.encerrado_novo_acao')}
              </a>
            </div>
          )}

          {/* UM UNICO CANAL: texto e/ou arquivos (operador, 2026-08-13).

              Havia DUAS caixas — "resposta ao comite" e "informacao complementar" —
              com a mesma forma, e NENHUMA aceitava arquivo. Quem precisava mandar um
              documento nao tinha como: o upload so existia no formulario de denuncia.

              Agora e uma caixa: digitar, anexar, ou os dois no mesmo envio.

              Some com a flag OFF (`messagingEnabled`) e em caso ENCERRADO — o servidor
              recusa os dois casos com 422, e oferecer o que ele recusa e o defeito que
              este guard evita. */}
          {decoded.messagingEnabled && !casoEncerrado && (
            <div className="review-section composer" data-testid="composer-unificado">
              <label className="review-label" htmlFor="status-resposta">
                {t('status.canal_label')}
              </label>
              <p className="composer__ajuda">{t('status.canal_ajuda')}</p>
              <SeloDeCifra
                texto={t('status.composer_selo', {
                  defaultValue: 'Fecha no seu aparelho antes de enviar',
                })}
              />
              <textarea
                id="status-resposta"
                className="form-input"
                rows={3}
                value={resposta}
                placeholder={t('status.responder_placeholder')}
                onChange={(e) => {
                  setResposta(e.target.value);
                  if (respostaState !== 'idle') setRespostaState('idle');
                }}
                disabled={respostaState === 'sending'}
              />

              {/* ANEXOS. O input fica atras do label por estilo, mas e um
                  `<input type=file>` de verdade: teclado e leitor de tela o alcancam. */}
              <div className="composer__anexos">
                <label className="composer__anexo-btn">
                  <input
                    type="file"
                    multiple
                    data-testid="composer-file"
                    onChange={(e) => {
                      adicionarArquivos(e.target.files);
                    }}
                    disabled={respostaState === 'sending'}
                  />
                  {t('status.canal_anexar')}
                </label>

                {arquivos.length > 0 && (
                  <ul className="composer__lista" data-testid="composer-lista">
                    {arquivos.map((f, i) => (
                      <li key={`${f.name}-${String(i)}`}>
                        <span className="composer__nome">{f.name}</span>
                        <span className="composer__tamanho">{formatarTamanho(f.size)}</span>
                        <button
                          type="button"
                          className="composer__remover"
                          aria-label={t('status.canal_remover', { nome: f.name })}
                          onClick={() => {
                            setArquivos(arquivos.filter((_, j) => j !== i));
                          }}
                          disabled={respostaState === 'sending'}
                        >
                          &times;
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* ARQUIVO GRANDE DEMAIS.

                  Aparece SO quando um arquivo escolhido passa de 25 MB, e o arquivo NAO
                  entra na fila: o servidor o recusaria com SIZE_REJECTED depois de o
                  denunciante esperar o upload inteiro.

                  As instrucoes reusam as chaves `step3.big_files_*` do formulario de
                  denuncia — nenhuma segunda redacao. Inclui o aviso de ANONIMATO, que e
                  a parte que nao pode se perder numa copia: link de nuvem pessoal pode
                  identificar quem enviou. So o passo 3 muda, porque no formulario o link
                  vai na descricao e aqui vai nesta conversa. */}
              {arquivoGrande !== null && (
                <div className="composer__grande" data-testid="composer-grande">
                  <Alert variant="warning" title={t('step3.big_files_summary')}>
                    <p style={{ margin: '0 0 8px' }}>
                      {t('status.canal_grande_rejeitado', {
                        nome: arquivoGrande.name,
                        tamanho: formatarTamanho(arquivoGrande.size),
                      })}
                    </p>
                    <p style={{ margin: '0 0 8px' }}>{t('step3.big_files_intro')}</p>
                    <ol style={{ margin: '0 0 8px', paddingLeft: 20 }}>
                      <li>{t('step3.big_files_step1')}</li>
                      <li>{t('step3.big_files_step2')}</li>
                      <li>{t('status.canal_grande_step3')}</li>
                    </ol>
                    <p style={{ margin: 0 }}>
                      <strong>{t('step3.big_files_warning_lead')}</strong>{' '}
                      {t('step3.big_files_warning_rest')}
                    </p>
                  </Alert>
                </div>
              )}

              {respostaState === 'sent' && (
                <div className="alert alert-success" role="status">
                  {t('status.responder_enviada')}
                </div>
              )}
              {respostaState === 'error' && (
                <div className="alert alert-warning" role="alert">
                  {t('status.canal_erro')}
                </div>
              )}
              <div className="composer__acao">
                <button
                  className="btn btn-primary"
                  data-testid="composer-enviar"
                  onClick={() => {
                    void handleEnviar();
                  }}
                  disabled={
                    (resposta.trim() === '' && arquivos.length === 0) || respostaState === 'sending'
                  }
                >
                  {respostaState === 'sending' ? <span className="spinner" /> : null}
                  {t('status.responder_enviar')}
                </button>
              </div>
            </div>
          )}

          <div className="alert alert-success" role="status">
            {t('step4.aviso_anonimato')}
          </div>
        </div>
      )}
    </div>
  );
}
